// The T2 entry cascade — ENGINE only (entry-cascade.md). Everything it renders
// comes from DATA: the event's competition catalog (roles/linkedTo/configs from
// /oireachtas/:id/competitions) and the Layer-1 reference (/oireachtas/reference).
// A syllabus change is a catalog edit — NEVER add per-event logic here.

export const birthYearOf = (birthday) => {
  const m = String(birthday || "").match(/(\d{4})/);
  return m ? Number(m[1]) : null;
};

const bornYearMatches = (bornYear, birthYear) => {
  if (bornYear == null || birthYear == null) return true;
  if (Array.isArray(bornYear)) return bornYear.map(Number).includes(birthYear);
  if (typeof bornYear === "object") {
    const from = bornYear.from != null ? Number(bornYear.from) : -Infinity;
    const to = bornYear.to != null ? Number(bornYear.to) : Infinity;
    return birthYear >= from && birthYear <= to;
  }
  return Number(bornYear) === birthYear;
};

// Single client-side source of truth for gender tokens — MUST match the
// server's normalizeGender (backend helpers/oireachtasEntryRules.js) or the
// client offers competitions checkout then rejects with GENDER_MISMATCH.
// Dancer docs store the AddDancer dropdown labels verbatim ("Girls/Ladies" /
// "Boys/Men"), so the compound forms must resolve or every real dancer reads
// as "unknown" and both gendered ladders get offered. trim() guards a
// stray-whitespace label. teamRules.js imports this — do not fork it.
export const normalizeGender = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (["female", "girl", "girls", "ladies", "lady", "woman", "f", "girls/ladies"].includes(v)) return "female";
  if (["male", "boy", "boys", "men", "man", "m", "boys/men"].includes(v)) return "male";
  return null; // unknown -> no constraint
};

const genderOk = (comp, dancerGender) => {
  const g = normalizeGender(dancerGender); // "female" | "male" | null
  if (["ladies", "girls"].includes(comp.gender)) return g !== "male";
  if (["men", "boys"].includes(comp.gender)) return g !== "female";
  return true; // mixed / boys_girls / na / unset
};

// DISPLAY only. Dancer docs store the AddDancer dropdown's compound labels
// ("Girls/Ladies" / "Boys/Men") because one option has to cover youth and adult
// dancers alike — but a roster row should read as just the half that fits the
// dancer. Who counts as an adult comes from the CATALOG's own Adult band (born
// on/before its `to` year), never a hard-coded age, so the label can't disagree
// with the competitions the cascade offers; a syllabus change moves both.
export const adultBornYear = (competitions) => {
  const years = (competitions || [])
    .filter((c) => String(c.ageGroup || "").toLowerCase() === "adult")
    .map((c) => {
      const b = c.bornYear;
      if (Array.isArray(b)) return Math.max(...b.map(Number));
      if (b && typeof b === "object") return b.to != null ? Number(b.to) : NaN;
      return b != null ? Number(b) : NaN;
    })
    .filter((y) => Number.isFinite(y));
  return years.length ? Math.max(...years) : null;
};

const GENDER_SPLIT = {
  "girls/ladies": ["Girls", "Ladies"],
  "boys/men": ["Boys", "Men"],
};

// Falls back to the stored value whenever we can't tell (no Adult band in this
// catalog, missing birthday, or a gender we don't own) — better a compound
// label than a confidently wrong one.
export const genderLabel = (dancer, adultTo) => {
  const raw = String((dancer && dancer.gender) || "").trim();
  const split = GENDER_SPLIT[raw.toLowerCase()];
  if (!split) return raw;
  const by = birthYearOf(dancer && dancer.birthday);
  if (adultTo == null || by == null) return raw;
  return by <= adultTo ? split[1] : split[0];
};

// Eligibility filter (generic): which of the event's competitions this dancer
// can be offered. Presentation-level — the server re-validates at checkout.
export const eligibleFor = (competitions, dancer) => {
  const by = birthYearOf(dancer.birthday);
  return competitions.filter((c) => genderOk(c, dancer.gender) && bornYearMatches(c.bornYear, by));
};

// Display order for the T2 "Competition…" dropdown: championship first, then
// Premiere, then anything else. The catalog's own array order can NOT carry
// this — competitions come back in insertion order and the template clone
// interleaves the groups (staging returns Solo Champ → Premiere → Solo Champ →
// Adult → Premiere …), so a dancer could be offered Premiere above their
// championship. Ranking is by `type`, so it holds for any syllabus; unranked
// types keep catalog order behind the ranked ones (sort is stable).
// Plausibility bound for a custom bpm — mirrors the server's isValidSpeed
// (helpers/oireachtasReference.js CUSTOM_BPM_MIN/MAX).
export const CUSTOM_BPM_MIN = 50;
export const CUSTOM_BPM_MAX = 130;

const PRIMARY_TYPE_RANK = { solo_champ: 0, premiere: 1 };
const primaryRank = (c) => {
  const r = PRIMARY_TYPE_RANK[c.type];
  return r == null ? 99 : r;
};

export const primariesFor = (competitions, dancer) =>
  eligibleFor(competitions, dancer)
    .filter((c) => c.role === "primary")
    .sort((a, b) => primaryRank(a) - primaryRank(b));

// Add-ons whose linked primary is the given competition (linkedTo names the
// primary's type or its display group).
export const addonsFor = (competitions, dancer, primary) =>
  eligibleFor(competitions, dancer).filter(
    (c) => c.role === "addon" && (c.linkedTo === primary.type || c.linkedTo === primary.group)
  );

// Which selector chain a competition type needs (ENTRY_SELECTORS). Steps are
// resolved against reference data by the helpers below.
export const ENTRY_SELECTORS = {
  solo_champ: ["setDance", "speed"],
  trad_set: ["setDance", "speed"],
  premiere: ["setDance"],
  treble_reel: [],
};
// A competition-level setDanceGate of "none" means the entry carries no
// set-dance selection at all (ECRO U6/U7 champs: light-shoe rounds, no set;
// U7 instead chooses its recall dance through roundDanceOptions below).
// Champ/trad-set only: the server's premiere branch always requires a
// selection regardless of gate, so honoring "none" on a premiere comp here
// would dead-end checkout with no control to satisfy it.
// Round-dance choice (competition.roundDanceOptions; ECRO 2026: the Adult
// Championships choose round 2, Slip Jig or Reel; the Under 7 championship
// chooses its round 3 recall, Light Jig or Slow Treble Jig). Independent of
// the set-dance chain; mirrors the server (helpers/oireachtasEntryRules.js):
// required and from the list when the comp offers it, never sent otherwise.
export const needsRoundDance = (comp) =>
  !!(comp && Array.isArray(comp.roundDanceOptions) && comp.roundDanceOptions.length);
// Null prototype: a stored key such as "constructor" must miss and show as
// stored, not resolve to an Object.prototype member.
export const ROUND_DANCE_LABELS = Object.freeze(Object.assign(Object.create(null), {
  reel: "Reel",
  slip_jig: "Slip Jig",
  light_jig: "Light Jig",
  slow_treble_jig: "Slow Treble Jig",
}));
// The round the choice applies to (competition.roundDanceRound). The Adult
// Championships were stored before that field existed: absent means round 2,
// as on the server (helpers/oireachtasRoundDance.roundDanceRoundOf).
export const roundDanceRoundOf = (comp) => {
  if (!needsRoundDance(comp)) return null;
  const n = Number(comp.roundDanceRound);
  return Number.isInteger(n) && n >= 1 ? n : 2;
};
// A row's round dance counts only when it is one of the comp's CURRENT
// options — a draft restored after the catalog changed must re-prompt
// rather than pass client-side and 400 at the server.
export const validRoundDance = (comp, value) =>
  needsRoundDance(comp) && !!value && comp.roundDanceOptions.includes(value);
export const roundDanceOptions = (comp, reference) => {
  if (!needsRoundDance(comp)) return [];
  const ref = (reference && reference.roundDances) || [];
  return comp.roundDanceOptions.map((key) => {
    const r = ref.find((x) => x.key === key);
    return { key, name: (r && r.name) || ROUND_DANCE_LABELS[key] || key };
  });
};

export const chainFor = (comp) =>
  comp && comp.setDanceGate === "none" && comp.type !== "premiere"
    ? []
    : ENTRY_SELECTORS[comp.type] || [];

// Set-dance options for a primary: premiere reads the competition's own
// premiereConfig list; champ/trad-set read the age-gated global list (the
// roster row carries setDanceGate, computed server-side against event year).
// A competition-level setDanceGate overrides the dancer-age gate (champ Trad
// Set specials and the Adult Championship are always-traditional). A
// competition-level setDanceOptions allowlist overrides both — a single
// entry means the comp dances a FIXED set (ECRO champ Trad Set U11+ =
// Jockey to the Fair; the UI auto-assigns it, no picker).
// The gate actually in force: the competition's own overrides the dancer-age
// one the roster row carries. Mirrors the server
// (helpers/oireachtasEntryRules.js) — it drives BOTH which sets are listed and
// whether they run at the fixed traditional tempo, so the two must never be
// derived separately.
export const effectiveGate = (comp, setDanceGate) =>
  (comp && comp.setDanceGate) || setDanceGate || null;

export const setDanceOptions = (comp, reference, setDanceGate) => {
  const all = reference.setDances || [];
  if (comp.type === "premiere") {
    const allowed = (comp.premiereConfig && comp.premiereConfig.danceOptions) || [];
    return allowed.length ? all.filter((s) => allowed.includes(s.key) || allowed.includes(s.name)) : all.filter((s) => s.traditional);
  }
  // Gate first, THEN the allowlist — the server applies both (gate AND
  // allowlist), so intersecting keeps client and server in lockstep even on
  // an admin-edited catalog where the allowlist strays outside the gate.
  const gate = effectiveGate(comp, setDanceGate);
  let list = all;
  if (gate === "traditional") list = list.filter((s) => s.traditional);
  else if (gate === "jig_only") list = list.filter((s) => s.timeSignature === "6/8");
  const allowed = comp.setDanceOptions || [];
  if (allowed.length) list = list.filter((s) => allowed.includes(s.key) || allowed.includes(s.name));
  return list;
};

// Speed options follow the CHOSEN set's time signature (reference edge).
//
// The fixed traditional tempo belongs to the GATE, not to the set. 2025 ECRO
// syllabus §9 (Final Round column) + §10: U8/U9, the Adult Championship and
// the Trad Set specials dance a "Trad Set" at its fixed tempo — everyone else
// dances "Set in Jig Time Only" (U10) or a "Set Dance" of choice (U11+), which
// take the time signature's speeds even when the dancer picks one of the seven
// traditional sets. All three 6/8 traditional sets are in the U10 jig list.
// Returns null = no speed step at all.
export const speedOptions = (setDance, reference, gate) => {
  if (!setDance || gate === "traditional") return null; // fixed traditional tempo
  return (reference.speedByTimeSignature || {})[setDance.timeSignature] || [];
};

// Label shown beside a set in the picker: its time signature, or "traditional"
// when the gate fixes the tempo (then the signature is not actionable).
export const setDanceMeta = (setDance, gate) =>
  gate === "traditional" ? "traditional" : setDance.timeSignature;

// Optional add-ons default to not entering. Only an explicit Add requires
// its dependent details; untouched optional controls never block a solo entry.
export const rowComplete = (row, comp, setDance, gate) => {
  if (!comp) return false;
  const chain = chainFor(comp);
  if (needsRoundDance(comp) && !validRoundDance(comp, row.roundDance)) return false;
  if (chain.includes("setDance") && !row.setKey) return false;
  if (chain.includes("speed") && setDance && gate !== "traditional") {
    // Custom bpm must be 2-3 digits AND plausible (50-130) — mirrors the
    // server guard (isValidSpeed); anything else would pass here and 400 at
    // checkout. "500" is not a tempo (staging user testing, 2026-08-20).
    if (!row.speed) return false;
    if (row.speed === "__custom") {
      if (!/^\d{2,3}$/.test(row.custom)) return false;
      const n = Number(row.custom);
      if (n < CUSTOM_BPM_MIN || n > CUSTOM_BPM_MAX) return false;
    }
  }
  if (row.treble === "add" && !row.trebleId) return false;
  return row.tradSet !== "add" || (!!row.tradSetId && !!row.tradSetKey);
};

// Serialize the UI rows into the backend cart-item shape. Only complete rows
// become items; the speed sent is the option value, a custom bpm number, or
// "traditional" for a traditional set.
// ctx.dancersById supplies the event-year set-dance gate.
export const rowsToCartItems = (rows, compsById, setsByKey, ctx = {}) => {
  const items = [];
  Object.values(rows).forEach((row) => {
    const comp = row.primaryId ? compsById[row.primaryId] : null;
    if (!comp) {
      // Add-later: addons hung off an already-PAID primary — the row carries
      // no (new) primary, just the linked addon(s). The backend guard accepts
      // an addon whose linked primary is on the dancer's paid entry.
      // A half-configured trad set (Add picked, set choice pending) serializes
      // NOTHING — mirroring rowComplete — so it can't silently drop from the
      // cart while the treble alone checks out.
      if (row.treble === "add" && !row.trebleId) return;
      if (row.tradSet === "add" && !(row.tradSetId && row.tradSetKey)) return;
      const competitions = [];
      const selections = [];
      if (row.treble === "add" && row.trebleId) competitions.push(row.trebleId);
      if (row.tradSet === "add" && row.tradSetId && row.tradSetKey) {
        competitions.push(row.tradSetId);
        selections.push({ competition_id: row.tradSetId, danceChoice: row.tradSetKey, speed: "traditional" });
      }
      if (competitions.length) items.push({ dancer_id: row.dancer_id, competitions, selections });
      return;
    }
    const dancer = ctx.dancersById ? ctx.dancersById[String(row.dancer_id)] : null;
    const setDance = row.setKey ? setsByKey[row.setKey] : null;
    const gate = effectiveGate(comp, dancer && dancer.setDanceGate);
    if (!rowComplete(row, comp, setDance, gate)) return;
    const competitions = [comp._id];
    if (row.treble === "add" && row.trebleId) competitions.push(row.trebleId);
    const selections = [];
    const roundDance = validRoundDance(comp, row.roundDance) ? row.roundDance : "";
    if (row.setKey || roundDance) {
      const speed = gate === "traditional" ? "traditional" : row.speed === "__custom" ? row.custom : row.speed;
      selections.push({
        competition_id: comp._id,
        ...(row.setKey ? { danceChoice: row.setKey } : {}),
        ...(row.setKey && chainFor(comp).includes("speed") ? { speed } : {}),
        ...(roundDance ? { roundDance } : {}),
      });
    }
    if (row.tradSet === "add" && row.tradSetId && row.tradSetKey) {
      competitions.push(row.tradSetId);
      selections.push({ competition_id: row.tradSetId, danceChoice: row.tradSetKey, speed: "traditional" });
    }
    items.push({ dancer_id: row.dancer_id, competitions, selections });
  });
  return items;
};

// Rehydrate UI rows from a persisted cart (draft restore). setsByKey +
// reference + dancersById let a numeric speed map back to its DROPDOWN
// option: "76" is a standard 2/4-4/4 option AND a plausible custom bpm, and
// mapping every numeric to __custom re-rendered a dropdown-picked speed as
// the free bpm input on return (staging user testing, 2026-08-20).
export const cartItemsToRows = (items, compsById, setsByKey = {}, reference = null, dancersById = {}) => {
  const rows = {};
  (items || []).forEach((it) => {
    const comps = (it.competitions || []).map((id) => compsById[String(id)]).filter(Boolean);
    const primary = comps.find((c) => c.role === "primary");
    const treble = comps.find((c) => c.role === "addon" && c.type === "treble_reel");
    const tradSet = comps.find((c) => c.role === "addon" && c.type === "trad_set");
    const sel = (it.selections || []).find((s) => primary && String(s.competition_id) === String(primary._id));
    const tradSel = (it.selections || []).find((s) => tradSet && String(s.competition_id) === String(tradSet._id));
    const speed = sel && sel.speed;
    const setDance = sel && sel.danceChoice ? setsByKey[sel.danceChoice] : null;
    const dancer = dancersById[String(it.dancer_id)];
    const gate = primary ? effectiveGate(primary, dancer && dancer.setDanceGate) : null;
    const stdOptions = (reference && speedOptions(setDance, reference, gate)) || [];
    const isStd = !!speed && stdOptions.includes(String(speed));
    rows[String(it.dancer_id)] = {
      dancer_id: String(it.dancer_id),
      primaryId: primary ? String(primary._id) : null,
      setKey: (sel && sel.danceChoice) || "",
      // Drop a restored value the comp no longer offers so the picker re-prompts.
      roundDance: primary && sel && validRoundDance(primary, sel.roundDance) ? sel.roundDance : "",
      speed: speed === "traditional" ? "" : isStd ? speed : speed && /^\d+$/.test(speed) ? "__custom" : speed || "",
      custom: speed && !isStd && /^\d+$/.test(speed) ? speed : "",
      treble: treble ? "add" : primary ? "none" : null,
      trebleId: treble ? String(treble._id) : null,
      tradSet: tradSet ? "add" : primary ? "none" : null,
      tradSetId: tradSet ? String(tradSet._id) : null,
      tradSetKey: (tradSel && tradSel.danceChoice) || "",
    };
  });
  return rows;
};
